import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash.isequal"

import { MIDDLE_PURPLE, STAT_COLOR_SCALE_START, STAT_COLOR_SCALE_END } from "shared/imports/sharedConstants"

export default class PieChart extends Component {
    static propTypes = {
        isLoading: PropTypes.bool.isRequired,
        updateGraph: PropTypes.func,
        data: PropTypes.object,
        selectedValues: PropTypes.array.isRequired,
        colorConfigs: PropTypes.object,
        maximumSlices: PropTypes.number,
    }

    static defaultProps = {
        colorConfigs: {
            secondaryColor: MIDDLE_PURPLE,
            primaryColor: STAT_COLOR_SCALE_END,
            darkerPrimaryColor: STAT_COLOR_SCALE_START,
        },
        maximumSlices: 10,
    }

    componentDidMount() {
        this.setupGraph()
    }

    componentDidUpdate(oldProps) {
        const loadingFinished = oldProps.isLoading && !this.props.isLoading
        const newData = !this.props.data.slice(0, 10).equals(oldProps.data.slice(0, 10))
        const colorChange = !isEqual(oldProps.colorConfigs, this.props.colorConfigs)

        if (loadingFinished || newData) {
            this.clearGraph()
            this.applyData()
        } else if (oldProps.selectedValues.length !== this.props.selectedValues.length || colorChange) {
            this.updateSlices({ colorChanged: colorChange })
        }
    }

    componentWillUnmount() {
        d3.select(".d3-tip").remove()
    }

    setupGraph() {
        this.svgWidth = parseInt(d3.select(this.node).style("width"))
        this.svgHeight = parseInt(d3.select(this.node).style("height"))

        d3.select(this.node)
            .attr("width", `${this.svgWidth}px`)
            .attr("height", `${this.svgHeight}px`)
            .append("g")
            .attr("transform", `translate(${this.svgWidth * 0.6},${this.svgHeight / 2})`)

        if (this.props.data.size) {
            this.applyData()
        }
    }

    clearGraph() {
        d3.select(this.node).selectAll(".legend").remove()
        d3.select(this.node).selectAll(".arc-group").remove()
    }

    processData(data) {
        // inner label threshold -- if the sliver is tinier,
        // then don't label it
        const innerLabelThreshold = 5

        // get percentages
        const totalCount = data.reduce((acc, datum) => acc + datum.get("value"), 0)

        const percentageData = data.slice(0, this.props.maximumSlices).map((datum) => {
            const percentNum = (datum.get("value") / totalCount) * 100
            const percentage = `${percentNum.toFixed(1)}%`
            return datum.set("percentage", percentage).set("innerLabel", percentNum > innerLabelThreshold)
        })

        const otherDataCount = data
            .slice(this.props.maximumSlices, data.size)
            .reduce((acc, datum) => acc + datum.get("value"), 0)

        let allData = percentageData

        if (otherDataCount) {
            allData = percentageData.push({
                short_display: "Other",
                long_display: "Other",
                value: otherDataCount,
                percentage: `${((otherDataCount / totalCount) * 100).toFixed(1)}%`,
                innerLabel: (otherDataCount / totalCount) * 100 > innerLabelThreshold,
            })
        }

        return allData.toJS()
    }

    updateSlices({ colorChanged = false }) {
        if (colorChanged) {
            this.colorFunc = this.getDefaultColorFunc()
            this.drawLegend()
        }

        d3.select(this.node)
            .selectAll(".slice")
            .attr("fill", (d, i) => {
                return this.props.selectedValues.includes(d.data.id)
                    ? this.props.colorConfigs.secondaryColor
                    : this.colorFunc(d.data, i)
            })
    }

    getDefaultColorFunc() {
        const numSlices =
            this.props.data.size < this.props.maximumSlices ? this.props.data.size : this.props.maximumSlices

        const defaultColorScale = d3.scale
            .linear()
            .interpolate(d3.interpolateHcl)
            .range([this.props.colorConfigs.darkerPrimaryColor, this.props.colorConfigs.primaryColor])
            .domain([0, numSlices])

        const defaultColorFunc = (d, i) => defaultColorScale(i)

        return defaultColorFunc
    }

    applyData() {
        this.data = this.processData(this.props.data)

        const radius = Math.min(this.svgWidth, this.svgHeight) / 2
        const arc = d3.svg
            .arc()
            .outerRadius(radius - 10)
            .innerRadius(0)

        const pieFunc = d3.layout
            .pie()
            .sort(null)
            .value((d) => d.value)

        this.colorFunc = this.props.colorConfigs.colorFunc || this.getDefaultColorFunc()

        // tooltip instantiation!
        const tooltip = d3
            .tip()
            .attr("class", "d3-tip")
            .html((d) => {
                const baseText = `<div>${d.data.long_display}: ${d.value}, ${d.data.percentage}</div>`
                let instructions = ""

                if (this.props.selectedValues.includes(d.data.id)) {
                    instructions = `<hr><i style="color:gray;font-weight:normal;">Click "Apply Selected" to change results...</i>`
                }

                return baseText.concat(instructions)
            })

        d3.select(this.node).call(tooltip)

        const arcGroups = d3
            .select(this.node)
            .select("g")
            .append("g")
            .attr("class", "arc-group")
            .selectAll(".arc")
            .data(pieFunc(this.data))
            .enter()
            .append("g")
            .attr("class", "arc")

        // now that we've made the arc groups, actually make the arcs
        arcGroups
            .append("path")
            .attr("d", arc)
            .attr("class", "slice")
            .on("click", (d) => {
                // can't just check on d.data.id because 0 is an id
                if (d.data.id != null) {
                    this.props.updateGraph(d.data.id)
                    tooltip.hide(d)
                    tooltip.show(d)
                }
            })
            .on("mouseover", function (d) {
                tooltip.show(d, this)
                d3.select(this).style("opacity", "0.5")
            })
            .on("mouseout", function (d) {
                tooltip.hide(d)
                d3.select(this).style("opacity", "1")
            })
            .attr("fill", (d, i) => this.colorFunc(d.data, i))
            .style("opacity", "0")
            .transition()
            .delay((_, i) => 10 * i)
            .attrTween("d", (d) => {
                const interpolate = d3.interpolate(d.startAngle + 0.1, d.endAngle)
                return (t) => {
                    d.endAngle = interpolate(t)
                    return arc(d)
                }
            })
            .style("opacity", "1")

        const innerLabelRadius = radius - 50
        const innerLabelArc = d3.svg.arc().outerRadius(innerLabelRadius).innerRadius(innerLabelRadius)

        const outerLabelRadius = radius - 10
        const outerLabelArc = d3.svg.arc().outerRadius(outerLabelRadius).innerRadius(outerLabelRadius)

        // now let's label things
        arcGroups
            .append("text")
            .attr("transform", (d) =>
                d.data.innerLabel
                    ? `translate(${innerLabelArc.centroid(d)})`
                    : `translate(${outerLabelArc.centroid(d)})`
            )
            .attr("dy", ".35em")
            .attr("text-anchor", (d) => (d.data.innerLabel ? "middle" : "start"))
            .attr("fill", (d) => (d.data.innerLabel ? "white" : "black"))
            .text((d) => {
                if (d.data.innerLabel) {
                    return d.data.percentage
                }
            })

        this.drawLegend()
    }

    drawLegend() {
        // now let's make a legend!
        d3.select(this.node).select(".legend").remove()

        const legend = d3.select(this.node).append("g").attr("class", "legend")

        legend
            .selectAll("rect")
            .data(this.data)
            .enter()
            .append("rect")
            .attr("y", (_, i) => i * 20 + 10)
            .attr("x", 10)
            .attr("width", 15)
            .attr("height", 15)
            .attr("fill", (d, i) => this.colorFunc(d, i))

        legend
            .selectAll("text")
            .data(this.data)
            .enter()
            .append("text")
            .attr("y", (_, i) => i * 20 + 10)
            .attr("x", 30)
            .attr("fill", (d, i) => this.colorFunc(d, i))
            .attr("dy", ".9em")
            .attr("text-anchor", "start")
            .attr("class", "pie-legend-text")
            .text((d) => d.short_display)
    }

    render() {
        return <svg ref={(bar) => (this.node = bar)} id={this.props.svgId} />
    }
}
